export interface ParsedMergeError {
  code?: string;
  message: string;
  isMergeConflict: boolean;
  conflictedFiles: string[];
}

function tryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extractDetailPayload(payload: unknown): unknown {
  if (payload && typeof payload === "object" && "detail" in payload) {
    return (payload as { detail: unknown }).detail;
  }
  return payload;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function isConflictFromMessage(message: string): boolean {
  const lowered = message.toLowerCase();
  return lowered.includes("merge conflict") || lowered.includes("has merge conflicts") || lowered.includes("conflicts");
}

/** Parse a raw merge error string (possibly JSON-wrapped) into structured fields. */
export function parseMergeError(raw: string | null | undefined): ParsedMergeError {
  const fallbackMessage = "Merge failed. Please try again.";
  if (!raw || raw.trim().length === 0) {
    return {
      message: fallbackMessage,
      isMergeConflict: false,
      conflictedFiles: [],
    };
  }

  const trimmed = raw.trim();
  const statusPrefixedMatch = trimmed.match(/^\d{3}:\s*([\s\S]+)$/);
  const jsonCandidate = statusPrefixedMatch ? statusPrefixedMatch[1] : trimmed;
  const parsedRoot = tryParseJson(jsonCandidate) ?? tryParseJson(trimmed);
  const detailPayload = extractDetailPayload(parsedRoot);

  let message = trimmed;
  let code: string | undefined;
  let conflictedFiles: string[] = [];

  if (typeof detailPayload === "string") {
    message = detailPayload;
  } else if (detailPayload && typeof detailPayload === "object") {
    const detail = detailPayload as Record<string, unknown>;
    if (typeof detail.message === "string" && detail.message.trim().length > 0) {
      message = detail.message;
    } else if (typeof detail.detail === "string" && detail.detail.trim().length > 0) {
      message = detail.detail;
    }

    if (typeof detail.code === "string" && detail.code.trim().length > 0) {
      code = detail.code;
    }

    conflictedFiles = toStringArray(detail.conflicted_files ?? detail.conflictedFiles);
  }

  const isMergeConflict =
    code === "MERGE_CONFLICT"
    || code === "MERGE_CONFLICT_RESOLUTION_FAILED"
    || isConflictFromMessage(message);

  if (message.trim().length === 0) {
    message = fallbackMessage;
  }

  return {
    code,
    message,
    isMergeConflict,
    conflictedFiles,
  };
}

export function getMergeErrorMessage(raw: string | null | undefined): string {
  return parseMergeError(raw).message;
}

export function isMergeConflictError(raw: string | null | undefined): boolean {
  return parseMergeError(raw).isMergeConflict;
}
